import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components';
import { WebhookEndpointRow } from '@/services/api/webhooks';

interface DisableWebhookEndpointModalProps {
  row: WebhookEndpointRow | null;
  onClose: () => void;
  onConfirm: (row: WebhookEndpointRow) => Promise<void>;
}

/** Disabling is immediate and irreversible — this confirm spells that out. */
const DisableWebhookEndpointModal: React.FC<
  DisableWebhookEndpointModalProps
> = ({ row, onClose, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  if (!row) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(row);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={busy ? undefined : onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-bg-white-0 px-6 py-5 shadow-card-lg">
          <div className="flex items-center gap-2">
            <i className="ri-forbid-line text-lg text-error-base" />
            <h2 className="text-lg font-semibold text-text-strong-950">
              Disable this endpoint?
            </h2>
          </div>
          <p className="mt-2 text-sm text-text-sub-600">
            <span className="break-all font-mono text-xs text-text-strong-950">
              {row.url}
            </span>{' '}
            will stop receiving events immediately. This cannot be undone — to
            resume deliveries, register the URL again (it gets a new signing
            secret).
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="white" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirm} disabled={busy}>
              {busy ? 'Disabling…' : 'Disable endpoint'}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default DisableWebhookEndpointModal;
